<script lang="ts" module>
	import type { ClassValue, HTMLTextareaAttributes } from 'svelte/elements';
	import type { VariantProps } from 'tailwind-variants';
	import { tv } from '../tv';
	import { CONTAINER_COLOR_VARIANTS, RADII_VARIANTS } from '../variants';

	export const textAreaVariants = tv({
		base: 'w-full max-w-full min-w-s0 grow bg-(--v-container) text-(--v-on-container) [scrollbar-color:var(--v-container-line)_transparent] [scrollbar-width:thin] [--ta-border:var(--border-width-b400)] placeholder:text-inherit placeholder:opacity-placeholder focus:outline-none focus:[box-shadow:inset_0_0_0_var(--ta-border)_var(--v-container-line)] focus:[--ta-border:var(--border-width-b600)] disabled-state',
		variants: {
			variant: CONTAINER_COLOR_VARIANTS,
			size: {
				'300':
					'min-h-[2rem] py-[calc((2rem_-_var(--text-t200--line-height))_/_2)] pr-s0 pl-s200 text-t200',
				'400':
					'min-h-[2.5rem] py-[calc((2.5rem_-_var(--text-t300--line-height))_/_2)] pr-s0 pl-s300 text-t300',
				'500':
					'min-h-[3rem] py-[calc((3rem_-_var(--text-t400--line-height))_/_2)] pr-s100 pl-s400 text-t400'
			},
			outlined: { true: '[box-shadow:inset_0_0_0_var(--ta-border)_var(--v-container-line)]' },
			radii: RADII_VARIANTS,
			resize: {
				Vertical: 'resize-y',
				Horizontal: 'resize-x',
				Both: 'resize',
				None: 'resize-none',
				Block: '[resize:block]',
				Inline: '[resize:inline]'
			}
		},
		defaultVariants: { variant: 'Background', size: '400', radii: '400', resize: 'Vertical' }
	});

	export interface TextAreaProps
		extends Omit<HTMLTextareaAttributes, 'class'>, VariantProps<typeof textAreaVariants> {
		class?: ClassValue;
		ref?: HTMLTextAreaElement | null;
	}
</script>

<script lang="ts">
	let {
		class: className,
		outlined,
		radii,
		ref = $bindable(null),
		resize,
		size,
		value = $bindable(),
		variant,
		...rest
	}: TextAreaProps = $props();

	export function focus(): void {
		ref?.focus();
	}
</script>

<textarea
	bind:this={ref}
	bind:value
	class={[textAreaVariants({ outlined, radii, resize, size, variant }), className]}
	{...rest}></textarea>

<style>
	textarea::-webkit-scrollbar {
		width: 0.5rem;
		height: 0.5rem;
	}
	textarea::-webkit-scrollbar-thumb,
	textarea::-webkit-scrollbar-track {
		border: 0.125rem solid transparent;
		background-clip: padding-box;
	}
	textarea::-webkit-scrollbar-thumb {
		background-color: var(--v-container-line);
		border-radius: var(--radius-pill);
		min-height: 1.25rem;
	}
	textarea::-webkit-scrollbar-track {
		background-color: transparent;
		border-radius: var(--radius-pill);
	}
	textarea::-webkit-scrollbar-corner {
		background-color: transparent;
	}
	textarea::-webkit-resizer {
		background: transparent;
		border-bottom: var(--ta-border) solid currentColor;
		border-right: var(--ta-border) solid currentColor;
		border-bottom-right-radius: inherit;
	}
</style>
